import { getExperiment, useExperiment } from '@eventbrite/statsig';
import { CheckoutType } from '../types';
import {
    CREATOR_VERIFICATION_BADGE,
    DETACH_INTERESTS_FROM_USER_MENU,
    LISTING_AVOID_RECHARGE_DATES_ON_ALL_EVENTS,
    LISTING_CREATE_QUALITY_SIGNAL,
    LISTING_FIRST_HIERARCHY_LEVEL_EXPERIMENT,
    LISTING_SECOND_HIERARCHY_LEVEL_EXPERIMENT,
    LISTING_TEST_AA_SSR_EXPERIMENT,
    LISTING_THIRD_HIERARCHY_LEVEL_EXPERIMENT,
    LISTING_VIDEO_AUTOPLAY_EXPERIMENT,
    ONE_TICKET_TYPE_EXPERIMENT,
    SEGMENTED_CAROUSEL_EXPERIMENT,
} from './constants';

type ExperimentDef = {
    experimentName: string;
    param: string;
    defaultValue: string | boolean;
};

export const genericGetRawVariant = <T>(experiment: ExperimentDef): T =>
    getExperiment(
        experiment.experimentName,
        experiment.param,
        experiment.defaultValue,
    );

export const genericGetBooleanExperiment = (
    experiment: ExperimentDef,
): boolean => {
    const value = genericGetRawVariant<string | boolean>(experiment);
    return value === true || value === 'true';
};

export const isAvoidRechargeDatesOnAllEvents = () =>
    genericGetBooleanExperiment(LISTING_AVOID_RECHARGE_DATES_ON_ALL_EVENTS);

export const useIsFirstHierarchyLevel = () => {
    const { value } = useExperiment<boolean | null>(
        LISTING_FIRST_HIERARCHY_LEVEL_EXPERIMENT.experimentName,
        LISTING_FIRST_HIERARCHY_LEVEL_EXPERIMENT.param,
        LISTING_FIRST_HIERARCHY_LEVEL_EXPERIMENT.defaultValue,
    );

    // Value could is a boolean if coming from Statsig or a string if coming from override.
    return value === true || (value as unknown as string) === 'true';
};

export const isSecondHierarchyLevel = () =>
    genericGetRawVariant<boolean>(LISTING_SECOND_HIERARCHY_LEVEL_EXPERIMENT);

export const isThirdHierarchyLevel = () =>
    genericGetRawVariant<boolean>(LISTING_THIRD_HIERARCHY_LEVEL_EXPERIMENT);

export const getCreatorQualitySignalVariant = () =>
    genericGetRawVariant<string>(LISTING_CREATE_QUALITY_SIGNAL);

export const isAutoplayVideoExperimentVariant = () =>
    genericGetRawVariant(LISTING_VIDEO_AUTOPLAY_EXPERIMENT);

export const useGetSegmentedCarouselExperiment = () =>
    genericGetRawVariant<string>(SEGMENTED_CAROUSEL_EXPERIMENT);

export const getCreatorVerificationBadge = () =>
    genericGetRawVariant<string>(CREATOR_VERIFICATION_BADGE);

export const useTestAAExperiment = () => {
    const { value } = useExperiment<boolean | null>(
        LISTING_TEST_AA_SSR_EXPERIMENT.experimentName,
        LISTING_TEST_AA_SSR_EXPERIMENT.param,
        LISTING_TEST_AA_SSR_EXPERIMENT.defaultValue,
    );

    return value === true || (value as unknown as string) === 'true';
};

export const isDetachInterestsExperimentEnabled = () =>
    genericGetBooleanExperiment(DETACH_INTERESTS_FROM_USER_MENU);

export const getCheckoutType = (isMultiTicketEnabled: boolean) => {
    if (isMultiTicketEnabled) return CheckoutType.OnlyCheckout;

    return genericGetRawVariant<CheckoutType>(ONE_TICKET_TYPE_EXPERIMENT);
};
